import { useMemo, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import AppLayout from '@/components/AppLayout'
import PageTitle from '@/components/PageTitle'
import {
  applyTableState,
  type Column,
  DataTable,
  DataTableToolbar,
  useTableState,
} from '@/components/blocks/data-table'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu'
import { EmptyState } from '@/components/ui/EmptyState'
import { ConfirmDialog, type ConfirmRequest } from '@/components/shared'
import MemberFormDialog, { type Member } from '@/components/members/MemberFormDialog'

interface MembersIndexProps {
  members: Member[]
  roles: { id: string; name: string }[]
}

/* Intl rather than a hand-rolled format, and the same shape the console uses. */
function formatDate(value: string): string {
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d)
}

/* Columns are data, so they sit outside the component: <DataTable> paints
 * exactly the rows it is handed and reports state upward, which is what lets
 * the sorting and searching below move into the controller later without the
 * component changing at all. */
const MEMBER_COLUMNS: Column<Member>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 'minmax(160px,1.2fr)',
    sortable: true,
    cell: (row) => <span className="text-fg font-medium">{row.name || '—'}</span>,
  },
  { key: 'email', header: 'Email', width: 'minmax(200px,1.4fr)', sortable: true },
  {
    key: 'role_name',
    header: 'Role',
    width: '140px',
    sortable: true,
    cell: (row) => <Badge variant="outline">{row.role_name}</Badge>,
  },
  {
    key: 'created_at',
    header: 'Joined',
    width: '140px',
    align: 'end',
    sortable: true,
    cell: (row) => <span className="text-fg-muted">{formatDate(row.created_at)}</span>,
  },
]

export default function MembersIndex({ members, roles }: MembersIndexProps) {
  const { state, setState } = useTableState({ pageSize: 25, sortKey: 'name', sortDir: 'asc' })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Member | null>(null)
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null)

  const table = useMemo(
    () => applyTableState(members, MEMBER_COLUMNS, state),
    [members, state],
  )

  const openNew = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (member: Member) => {
    setEditing(member)
    setDialogOpen(true)
  }

  /* Was window.confirm(). Destructive actions get a real dialog — the same one
     Game Config uses to stage a change. */
  const requestDelete = (member: Member) =>
    setConfirmRequest({
      title: `Delete ${member.name || member.email}?`,
      description: 'Their account is removed immediately. This cannot be undone.',
      confirmLabel: 'Delete Member',
      destructive: true,
      onConfirm: () => router.delete(`/members/${member.id}`, { preserveScroll: true }),
    })

  return (
    <AppLayout
      header={<PageTitle title="Members" subtitle={`${members.length} members`} />}
      headerActions={
        <Button onClick={openNew}>
          <Plus />
          New Member
        </Button>
      }
    >
      <Head title="Members" />

      <div className="space-y-3">
        <DataTableToolbar
          state={state}
          onStateChange={setState}
          searchPlaceholder="Search name or email…"
        />

        <DataTable
          columns={MEMBER_COLUMNS}
          rows={table.rows}
          total={table.total}
          state={state}
          onStateChange={setState}
          rowKey={(row) => row.id}
          minWidth={760}
          empty={
            <EmptyState
              title={members.length === 0 ? 'No members yet.' : 'No members match your search.'}
              description={
                members.length === 0 ? 'Invite someone to get started.' : undefined
              }
              action={
                members.length === 0 ? undefined : (
                  <Button variant="outline" onClick={() => setState({ search: '', filters: {} })}>
                    Clear Filters
                  </Button>
                )
              }
            />
          }
          rowActions={(row) => (
            <DropdownMenu
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Actions for ${row.name || row.email}`}
                >
                  <MoreHorizontal />
                </Button>
              }
            >
              <DropdownMenuItem onClick={() => openEdit(row)}>
                <Pencil />
                Edit Member
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onClick={() => requestDelete(row)}>
                <Trash2 />
                Delete Member
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        />
      </div>

      {dialogOpen && (
        <MemberFormDialog
          key={editing?.id ?? 'new'}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          member={editing}
          roles={roles}
        />
      )}

      <ConfirmDialog request={confirmRequest} onClose={() => setConfirmRequest(null)} />
    </AppLayout>
  )
}
